import { Outlet, Link, useRouterState } from '@tanstack/react-router';
import { Container } from '@/components/ui/container';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { cn } from '@/lib/cn';
import { ADMIN_NAV, ADMIN_NAV_FLAT, isActive, pendingCount } from '@/features/admin/nav';
import { useAdminOverview } from '@/features/admin/api';

/** The count pill on a nav item that has pending work. */
function PendingBadge({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span
      className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-pill border-2 border-canvas bg-critical px-1 text-[0.6rem] font-bold leading-none text-on-solid tabular-nums"
      aria-label={`${n} pending`}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

export function AdminShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: overview } = useAdminOverview();

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="sticky top-0 z-40 border-b-2 border-ink bg-surface">
        <Container className="flex h-14 items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold">
            <span className="rounded-blob inline-block size-3.5 bg-critical" aria-hidden />
            Nearmirer admin
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app" className="text-xs font-semibold text-ink-soft hover:text-ink">
              Back to app
            </Link>
            <ThemeToggle />
          </div>
        </Container>
        {/* Mobile / tablet: flat pill nav */}
        <Container className="lg:hidden">
          <nav className="flex gap-1 overflow-x-auto pb-2">
            {ADMIN_NAV_FLAT.map((n) => {
              const pending = pendingCount(n, overview);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={cn(
                    'flex items-center gap-1.5 whitespace-nowrap rounded-pill px-3 py-1 text-sm font-semibold transition-colors',
                    isActive(n, pathname)
                      ? 'bg-ink text-on-solid'
                      : 'text-ink-soft hover:bg-surface-sunken',
                  )}
                >
                  {n.label}
                  {pending > 0 && (
                    <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-pill bg-critical px-1 text-[0.6rem] font-bold leading-none text-on-solid tabular-nums">
                      {pending > 99 ? '99+' : pending}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </Container>
      </header>

      <div className="mx-auto flex w-full max-w-6xl flex-1">
        {/* Desktop: grouped left sidebar */}
        <aside className="hidden w-52 shrink-0 border-r-2 border-hairline px-3 py-6 lg:block">
          <nav className="flex flex-col gap-5">
            {ADMIN_NAV.map((group) => (
              <div key={group.title}>
                <p className="px-2 pb-1 text-[0.65rem] font-semibold uppercase tracking-wide text-ink-faint">
                  {group.title}
                </p>
                <div className="flex flex-col gap-0.5">
                  {group.items.map((n) => (
                    <Link
                      key={n.to}
                      to={n.to}
                      className={cn(
                        'flex items-center gap-2 rounded-inset px-2 py-1.5 text-sm font-semibold transition-colors',
                        isActive(n, pathname)
                          ? 'bg-ink text-on-solid'
                          : 'text-ink-soft hover:bg-surface-sunken',
                      )}
                    >
                      {n.label}
                      <PendingBadge n={pendingCount(n, overview)} />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
